import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Calendar, Clock, Users, Video, User, Loader2, CheckCircle, Play, QrCode, Building, Wallet, Landmark, ArrowLeft, Mail, Phone, CreditCard, Copy } from 'lucide-react';
import { toast } from 'sonner';
import useStoreSubdomain from '@/hooks/useStoreSubdomain';

export default function WebinarDetail() {
  const { webinarId } = useParams();
  const subdomain = useStoreSubdomain();
  const [webinar, setWebinar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showRegister, setShowRegister] = useState(false);
  const [form, setForm] = useState(() => {
    let init = { customer_name: '', customer_email: '', customer_phone: '', payment_method: 'qris' };
    const saved = localStorage.getItem('user');
    if (saved) { try { const u = JSON.parse(saved); if (u.role === 'customer') { init.customer_name = u.name || ''; init.customer_email = u.email || ''; } } catch {} }
    return init;
  });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // { type: 'free' | 'paid', registration, order, payment_url, message }

  // Autofill phone from customer profile
  useEffect(() => {
    const saved = localStorage.getItem('user');
    if (saved) { try { const u = JSON.parse(saved); if (u.role === 'customer' && localStorage.getItem('token')) { fetch('/api/customer/profile', { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }).then(r => r.ok ? r.json() : null).then(d => { if (d?.customer?.phone) setForm(f => ({ ...f, customer_phone: f.customer_phone || d.customer.phone })); }).catch(() => {}); } } catch {} }
  }, []);

  useEffect(() => {
    if (!subdomain || !webinarId) return;
    setLoading(true);
    fetch(`/api/store/${subdomain}/webinars/${webinarId}`)
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(data => setWebinar(data))
      .catch(() => toast.error('Webinar tidak ditemukan'))
      .finally(() => setLoading(false));
  }, [subdomain, webinarId]);

  const handleRegister = async () => {
    if (!form.customer_name || !form.customer_email) return toast.error('Nama dan email wajib diisi');
    setSubmitting(true);
    try {
      const res = await fetch(`/api/store/${subdomain}/webinars/${webinarId}/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Gagal mendaftar');

      setShowRegister(false);

      if (data.payment_url) {
        setResult({ type: 'paid', ...data });
      } else {
        setResult({ type: 'free', ...data });
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const fmt = (v) => new Intl.NumberFormat('id-ID').format(v || 0);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin" size={32} /></div>;
  if (!webinar) return <div className="min-h-screen flex items-center justify-center text-slate-500">Webinar tidak ditemukan</div>;

  const isFull = webinar.remaining_slots === 0;
  const isCompleted = webinar.status === 'completed';
  const canRegister = !isCompleted && !isFull && ['upcoming', 'live'].includes(webinar.status);
  const progressPercent = webinar.max_participants > 0 ? Math.min(100, (webinar.registration_count / webinar.max_participants) * 100) : 0;
  const isPaid = webinar.price > 0;
  const paymentMethodLabels = { qris: 'QRIS', va: 'Virtual Account', ewallet: 'E-Wallet', bank_transfer: 'Transfer Bank' };

  // ===== THANK YOU PAGE =====
  if (result) {
    const isFree = result.type === 'free';
    const reg = result.registration || {};
    const order = result.order || {};
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-2xl mx-auto px-4 py-12">
          {/* Back link */}
          <Link to={`/store/${subdomain}`} className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700 mb-8">
            <ArrowLeft size={16} /> Kembali ke Toko
          </Link>

          {/* Success Banner */}
          <Card className="border-slate-200 shadow-sm mb-6">
            <CardContent className="py-10 text-center">
              <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                <CheckCircle size={40} className="text-emerald-500" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-1">
                {isFree ? 'Pendaftaran Berhasil!' : 'Pendaftaran Diterima!'}
              </h2>
              <p className="text-slate-500">
                {isFree
                  ? 'Anda berhasil terdaftar di webinar ini'
                  : 'Silakan selesaikan pembayaran untuk mengkonfirmasi pendaftaran'}
              </p>
            </CardContent>
          </Card>

          {/* Webinar Info */}
          <Card className="mb-6">
            <CardContent className="p-6">
              <h3 className="font-semibold text-lg mb-4">Detail Webinar</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-slate-500">Webinar</span>
                  <span className="font-medium text-right max-w-[60%]">{webinar.title}</span>
                </div>
                {webinar.speaker_name && (
                  <div className="flex justify-between">
                    <span className="text-slate-500">Pembicara</span>
                    <span className="font-medium">{webinar.speaker_name}</span>
                  </div>
                )}
                {webinar.scheduled_at && (
                  <div className="flex justify-between">
                    <span className="text-slate-500">Jadwal</span>
                    <span className="font-medium">{new Date(webinar.scheduled_at).toLocaleDateString('id-ID', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-slate-500">Durasi</span>
                  <span className="font-medium">{webinar.duration_minutes} menit</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Tipe</span>
                  <span className="font-medium">{webinar.type === 'live' ? 'Live' : webinar.type === 'recorded' ? 'Rekaman' : 'Live + Replay'}</span>
                </div>
                <hr />
                <div className="flex justify-between">
                  <span className="text-slate-500">Harga</span>
                  <span className="font-bold text-lg text-violet-600">{isPaid ? `Rp ${fmt(webinar.price)}` : 'GRATIS'}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Registrant Info */}
          <Card className="mb-6">
            <CardContent className="p-6">
              <h3 className="font-semibold text-lg mb-4">Data Pendaftar</h3>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <User size={16} className="text-slate-400" />
                  <span>{reg.participant_name || form.customer_name}</span>
                </div>
                <div className="flex items-center gap-3">
                  <Mail size={16} className="text-slate-400" />
                  <span>{reg.participant_email || form.customer_email}</span>
                </div>
                {(reg.participant_phone || form.customer_phone) && (
                  <div className="flex items-center gap-3">
                    <Phone size={16} className="text-slate-400" />
                    <span>{reg.participant_phone || form.customer_phone}</span>
                  </div>
                )}
                <div className="flex items-center gap-3">
                  <Badge className={isFree ? 'bg-green-100 text-green-800 border-0' : 'bg-amber-100 text-amber-800 border-0'}>
                    {isFree ? '✅ Terdaftar' : '⏳ Menunggu Pembayaran'}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Payment Section (paid only) */}
          {!isFree && (
            <Card className="mb-6 border-violet-200">
              <CardContent className="p-6">
                <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                  <CreditCard size={20} className="text-violet-600" /> Pembayaran
                </h3>
                <div className="space-y-3 mb-4">
                  <div className="flex justify-between">
                    <span className="text-slate-500">No. Order</span>
                    <div className="flex items-center gap-2">
                      <span className="font-mono font-medium">{order.order_number}</span>
                      <button onClick={() => { navigator.clipboard.writeText(order.order_number); toast.success('Disalin!'); }} className="text-slate-400 hover:text-slate-600">
                        <Copy size={14} />
                      </button>
                    </div>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Metode Pembayaran</span>
                    <span className="font-medium">{paymentMethodLabels[order.payment_method] || order.payment_method}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Total</span>
                    <span className="font-bold text-lg text-violet-600">Rp {fmt(order.total)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Status</span>
                    <Badge className="bg-amber-100 text-amber-800 border-0">Menunggu Pembayaran</Badge>
                  </div>
                </div>
                {result.payment_url && (
                  <a href={result.payment_url} target="_blank" rel="noopener noreferrer">
                    <Button className="w-full bg-violet-600 hover:bg-violet-700" size="lg">
                      <CreditCard size={16} className="mr-2" /> Bayar Sekarang — Rp {fmt(order.total)}
                    </Button>
                  </a>
                )}
              </CardContent>
            </Card>
          )}

          {/* What's Next */}
          <Card className="bg-slate-50 border-slate-200">
            <CardContent className="p-6">
              <h3 className="font-semibold mb-3">📋 Langkah Selanjutnya</h3>
              <div className="space-y-2 text-sm text-slate-600">
                {isFree ? (
                  <>
                    <p>✅ Pendaftaran Anda sudah dikonfirmasi</p>
                    <p>📧 Detail webinar telah dikirim ke email Anda</p>
                    <p>🔔 Anda akan menerima reminder sebelum webinar dimulai</p>
                    <p>📹 Link meeting akan dikirim melalui WhatsApp/Email</p>
                  </>
                ) : (
                  <>
                    <p>1️⃣ Selesaikan pembayaran melalui link di atas</p>
                    <p>2️⃣ Setelah pembayaran dikonfirmasi, Anda akan menerima email konfirmasi</p>
                    <p>3️⃣ Link meeting akan dikirim via WhatsApp/Email sebelum webinar dimulai</p>
                    <p>4️⃣ Anda akan menerima reminder menjelang hari H</p>
                  </>
                )}
              </div>
            </CardContent>
          </Card>

          <div className="text-center mt-6">
            <Link to={`/store/${subdomain}`}>
              <Button variant="outline">Kembali ke Toko</Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // ===== MAIN WEBINAR DETAIL PAGE =====

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Hero */}
      <div className="bg-gradient-to-r from-violet-600 to-indigo-600 text-white">
        <div className="max-w-4xl mx-auto px-4 py-12 md:py-16">
          <div className="flex flex-col md:flex-row gap-8 items-center">
            <div className="flex-1">
              <Badge className="bg-white/20 text-white mb-3">
                {webinar.type === 'live' ? '🔴 Live Webinar' : webinar.type === 'recorded' ? '📹 Recorded' : '🔴 Live + Replay'}
              </Badge>
              <h1 className="text-3xl md:text-4xl font-bold mb-4">{webinar.title}</h1>
              {webinar.scheduled_at && (
                <div className="flex items-center gap-4 text-white/80 mb-2">
                  <span className="flex items-center gap-1"><Calendar size={16} /> {webinar.scheduled_at}</span>
                  <span className="flex items-center gap-1"><Clock size={16} /> {webinar.duration_minutes} menit</span>
                </div>
              )}
              <div className="flex items-center gap-4 text-white/80">
                <span className="flex items-center gap-1"><Users size={16} /> {webinar.registration_count} peserta terdaftar</span>
              </div>
            </div>
            {webinar.thumbnail && (
              <img src={webinar.thumbnail} alt="" className="w-full md:w-72 h-48 object-cover rounded-xl shadow-lg" />
            )}
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="grid md:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="md:col-span-2 space-y-6">
            {/* Description */}
            {webinar.description && (
              <Card>
                <CardContent className="p-6">
                  <h2 className="text-lg font-semibold mb-3">Tentang Webinar</h2>
                  <p className="text-slate-600 whitespace-pre-wrap">{webinar.description}</p>
                </CardContent>
              </Card>
            )}

            {/* Speaker */}
            {webinar.speaker_name && (
              <Card>
                <CardContent className="p-6">
                  <h2 className="text-lg font-semibold mb-3">Pembicara</h2>
                  <div className="flex items-start gap-4">
                    {webinar.speaker_photo ? (
                      <img src={webinar.speaker_photo} alt="" className="w-16 h-16 rounded-full object-cover" />
                    ) : (
                      <div className="w-16 h-16 rounded-full bg-violet-100 flex items-center justify-center">
                        <User size={24} className="text-violet-500" />
                      </div>
                    )}
                    <div>
                      <h3 className="font-semibold">{webinar.speaker_name}</h3>
                      {webinar.speaker_bio && <p className="text-sm text-slate-600 mt-1">{webinar.speaker_bio}</p>}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Sessions */}
            {webinar.sessions?.length > 0 && (
              <Card>
                <CardContent className="p-6">
                  <h2 className="text-lg font-semibold mb-3">Jadwal Sesi</h2>
                  <div className="space-y-3">
                    {webinar.sessions.map((s, i) => (
                      <div key={s.id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                        <div className="w-8 h-8 rounded-full bg-violet-100 flex items-center justify-center text-sm font-bold text-violet-600">{i + 1}</div>
                        <div className="flex-1">
                          <p className="font-medium text-sm">{s.title}</p>
                          {s.scheduled_at && <p className="text-xs text-slate-500">{s.scheduled_at} • {s.duration_minutes} menit</p>}
                          {s.description && <p className="text-xs text-slate-500 mt-1">{s.description}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Replay */}
            {isCompleted && webinar.replay_url && (
              <Card className="border-green-200 bg-green-50">
                <CardContent className="p-6">
                  <h2 className="text-lg font-semibold mb-2 flex items-center gap-2"><Play size={20} className="text-green-600" /> Replay Tersedia</h2>
                  <p className="text-sm text-slate-600 mb-3">Webinar ini sudah selesai. Tonton replaynya di sini:</p>
                  <Button asChild><a href={webinar.replay_url} target="_blank" rel="noopener noreferrer">Tonton Replay</a></Button>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            <Card className="sticky top-4">
              <CardContent className="p-6 space-y-4">
                <div className="text-center">
                  <p className="text-3xl font-bold text-violet-600">
                    {webinar.price > 0 ? `Rp ${webinar.price.toLocaleString('id-ID')}` : 'GRATIS'}
                  </p>
                </div>

                {/* Quota progress */}
                {webinar.max_participants > 0 && (
                  <div>
                    <div className="flex justify-between text-xs text-slate-500 mb-1">
                      <span>{webinar.registration_count} terdaftar</span>
                      <span>{webinar.max_participants} kuota</span>
                    </div>
                    <div className="w-full bg-slate-200 rounded-full h-2">
                      <div className="bg-violet-500 h-2 rounded-full transition-all" style={{ width: `${progressPercent}%` }} />
                    </div>
                    {webinar.remaining_slots >= 0 && (
                      <p className={`text-xs mt-1 ${webinar.remaining_slots <= 5 ? 'text-red-500 font-medium' : 'text-slate-500'}`}>
                        {webinar.remaining_slots === 0 ? 'Kuota penuh!' : `${webinar.remaining_slots} slot tersisa`}
                      </p>
                    )}
                  </div>
                )}

                {canRegister ? (
                  <Button className="w-full bg-violet-600 hover:bg-violet-700" size="lg" onClick={() => setShowRegister(true)}>
                    {webinar.price > 0 ? 'Daftar & Bayar' : 'Daftar Sekarang'}
                  </Button>
                ) : isCompleted ? (
                  <Badge className="w-full justify-center py-2 bg-green-100 text-green-800">✅ Webinar Selesai</Badge>
                ) : isFull ? (
                  <Badge className="w-full justify-center py-2 bg-red-100 text-red-800">Kuota Penuh</Badge>
                ) : null}

                <div className="space-y-2 text-sm text-slate-600">
                  <div className="flex items-center gap-2"><Video size={14} /> {webinar.type === 'live' ? 'Live Webinar' : webinar.type === 'recorded' ? 'Recorded' : 'Live + Replay'}</div>
                  {webinar.scheduled_at && <div className="flex items-center gap-2"><Calendar size={14} /> {webinar.scheduled_at}</div>}
                  <div className="flex items-center gap-2"><Clock size={14} /> {webinar.duration_minutes} menit</div>
                  {webinar.speaker_name && <div className="flex items-center gap-2"><User size={14} /> {webinar.speaker_name}</div>}
                  <div className="flex items-center gap-2"><CheckCircle size={14} /> Sertifikat kehadiran</div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Registration Dialog */}
      <Dialog open={showRegister} onOpenChange={setShowRegister}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Daftar Webinar</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="p-3 bg-violet-50 rounded-lg">
              <p className="font-medium text-sm text-violet-900">{webinar.title}</p>
              <p className="text-sm text-violet-600 mt-1">
                {isPaid ? `Rp ${fmt(webinar.price)}` : 'Gratis'}
                {webinar.scheduled_at && ` • ${new Date(webinar.scheduled_at).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' })}`}
              </p>
            </div>
            <div><Label>Nama Lengkap *</Label><Input value={form.customer_name} onChange={e => setForm({ ...form, customer_name: e.target.value })} placeholder="Nama lengkap Anda" /></div>
            <div><Label>Email *</Label><Input type="email" value={form.customer_email} onChange={e => setForm({ ...form, customer_email: e.target.value })} placeholder="email@contoh.com" /></div>
            <div><Label>No. WhatsApp</Label><Input value={form.customer_phone} onChange={e => setForm({ ...form, customer_phone: e.target.value })} placeholder="08xxxxxxxxxx" /></div>

            {/* Payment Method - only for paid webinars */}
            {isPaid && (
              <div>
                <Label className="mb-2 block">Metode Pembayaran *</Label>
                <RadioGroup value={form.payment_method} onValueChange={v => setForm({ ...form, payment_method: v })}>
                  <div className="grid grid-cols-2 gap-2">
                    <label className={`flex items-center gap-2 p-3 rounded-lg border-2 cursor-pointer transition-all text-sm ${form.payment_method === 'qris' ? 'border-violet-500 bg-violet-50' : 'border-slate-200 hover:border-slate-300'}`}>
                      <RadioGroupItem value="qris" />
                      <QrCode size={16} className="text-slate-600" />
                      <span className="font-medium">QRIS</span>
                    </label>
                    <label className={`flex items-center gap-2 p-3 rounded-lg border-2 cursor-pointer transition-all text-sm ${form.payment_method === 'va' ? 'border-violet-500 bg-violet-50' : 'border-slate-200 hover:border-slate-300'}`}>
                      <RadioGroupItem value="va" />
                      <Building size={16} className="text-slate-600" />
                      <span className="font-medium">Virtual Account</span>
                    </label>
                    <label className={`flex items-center gap-2 p-3 rounded-lg border-2 cursor-pointer transition-all text-sm ${form.payment_method === 'ewallet' ? 'border-violet-500 bg-violet-50' : 'border-slate-200 hover:border-slate-300'}`}>
                      <RadioGroupItem value="ewallet" />
                      <Wallet size={16} className="text-slate-600" />
                      <span className="font-medium">E-Wallet</span>
                    </label>
                    <label className={`flex items-center gap-2 p-3 rounded-lg border-2 cursor-pointer transition-all text-sm ${form.payment_method === 'bank_transfer' ? 'border-violet-500 bg-violet-50' : 'border-slate-200 hover:border-slate-300'}`}>
                      <RadioGroupItem value="bank_transfer" />
                      <Landmark size={16} className="text-slate-600" />
                      <span className="font-medium">Transfer Bank</span>
                    </label>
                  </div>
                </RadioGroup>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRegister(false)}>Batal</Button>
            <Button onClick={handleRegister} disabled={submitting} className="bg-violet-600 hover:bg-violet-700">
              {submitting && <Loader2 size={14} className="mr-1 animate-spin" />}
              {isPaid ? `Daftar & Bayar Rp ${fmt(webinar.price)}` : 'Daftar Gratis'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
